import React from "react";
import { EditOutlined, EyeOutlined, UndoOutlined, SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import { cdnUrl, basenameFromStorageKey } from "../../../config/mediaPaths";
import styles from "./ListView.module.css";
import { getGuidedJourneyBusinessTarget } from "../../../config/guidedJourneyConfig";

function formatUpdated(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  const now = Date.now();
  const diff = now - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const ListView = React.memo(function ListView({
  shops,
  isStoreDeleted,
  onManage,
  onEdit,
  onDelete,
  onRestore,
  onPreview,
  statusLabel,
}) {
  return (
    <div className={styles.list}>
      <div className={styles.listHeader}>
        <div />
        <div>Name</div>
        <div className={styles.listHideMobile}>Status</div>
        <div className={styles.listHideMobile}>Updated</div>
        <div />
      </div>

      {shops.length === 0 ? (
        <div className={styles.listEmptyRow}>
          <SearchOutlined className={styles.listEmptyIcon} />
          <span className={styles.listEmptyLabel}>No {statusLabel || "matching"} businesses</span>
        </div>
      ) : (
        shops.map((shop) => {
          const deleted = isStoreDeleted(shop);
          const bizImgUrl = shop.bizImageKey && shop.hubId && shop.bizId
            ? cdnUrl({ entity: "biz", fileType: "media", hubId: shop.hubId, bizId: shop.bizId, fileName: basenameFromStorageKey(shop.bizImageKey) })
            : null;
          const initial = (shop.name || "?")[0];
          const statusText = deleted
            ? "Deleted"
            : shop.bizOpenStatus === false
              ? "Closed"
              : shop.BizSettings?.bizLive === true
                ? "Live"
                : "Draft";

          return (
            <div
              id={getGuidedJourneyBusinessTarget(shop.bizId)}
              key={shop.bizId}
              className={`${styles.listRow} ${deleted ? styles.listRowDeleted : ""}`}
            >
              <div>
                {bizImgUrl ? (
                  <img src={bizImgUrl} alt="" className={styles.listThumb} />
                ) : (
                  <div className={styles.listThumbPlaceholder}>{initial}</div>
                )}
              </div>
              <div
                className={styles.listName}
                onClick={() => !deleted && onManage?.(shop)}
                style={{ cursor: deleted ? "default" : "pointer" }}
                role="button"
                tabIndex={deleted ? -1 : 0}
                onKeyDown={(e) => { if (e.key === "Enter" && !deleted) onManage?.(shop); }}
              >
                {shop.name}
              </div>
              <div className={`${styles.listHideMobile} ${styles.listStatus} ${
                deleted
                  ? styles.listStatusDeleted
                  : shop.bizOpenStatus === false
                    ? styles.listStatusDraft
                    : shop.BizSettings?.bizLive === true
                      ? styles.listStatusLive
                      : styles.listStatusDraft
              }`}>
                {statusText}
              </div>
              <div className={`${styles.listHideMobile} ${styles.listUpdated}`}>{formatUpdated(shop.updatedAt)}</div>
              <div className={styles.listActions}>
                {deleted ? (
                  <>
                    <button
                      className={styles.listActionBtn}
                      onClick={(e) => { e.stopPropagation(); onPreview?.(shop); }}
                      aria-label="Preview"
                      title="Preview"
                    >
                      <EyeOutlined />
                    </button>
                    <button
                      className={styles.listActionBtn}
                      onClick={(e) => { e.stopPropagation(); onRestore?.(shop); }}
                      aria-label="Restore"
                      title="Restore"
                    >
                      <UndoOutlined />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className={styles.listActionBtn}
                      onClick={(e) => { e.stopPropagation(); onEdit?.(shop); }}
                      aria-label="Edit"
                      title="Edit"
                    >
                      <EditOutlined />
                    </button>
                    <button
                      className={`${styles.listActionBtn} ${styles.listActionBtnDanger}`}
                      onClick={(e) => { e.stopPropagation(); onDelete?.(shop); }}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <DeleteOutlined />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
});

export default ListView;
